/**
 * Owner: Masa Bayu
 * File: qx-draft-marks.ts
 * Date:Time: 2026-10-16 01:09
 * Description: Draft DOM ↔ QXW inline marks (bold…super/sub · color).
 */

import {
  createQXTextFormatMark,
  createQXTextInline,
  createQXTextMark,
  type QXTextInline,
  type QXTextMark,
} from '@qx/qxw-core';

type MarkBag = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  superscript: boolean;
  subscript: boolean;
  textColor: string | null;
  highlight: string | null;
};

const EMPTY_MARKS: MarkBag = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  superscript: false,
  subscript: false,
  textColor: null,
  highlight: null,
};

function cssColorToHex(value: string): string | null {
  const raw = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) return `#${raw.slice(1).toUpperCase()}`;
  if (/^#[0-9A-Fa-f]{3}$/.test(raw)) {
    const r = raw[1]!;
    const g = raw[2]!;
    const b = raw[3]!;
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  const rgb = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!rgb) return null;
  const hex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${hex(Number(rgb[1]))}${hex(Number(rgb[2]))}${hex(Number(rgb[3]))}`;
}

function mergeMarks(base: MarkBag, el: HTMLElement): MarkBag {
  const tag = el.tagName.toLowerCase();
  const next = { ...base };
  if (tag === 'b' || tag === 'strong') next.bold = true;
  if (tag === 'i' || tag === 'em') next.italic = true;
  if (tag === 'u') next.underline = true;
  if (tag === 's' || tag === 'strike' || tag === 'del') next.strikethrough = true;
  if (tag === 'sup') {
    next.superscript = true;
    next.subscript = false;
  }
  if (tag === 'sub') {
    next.subscript = true;
    next.superscript = false;
  }
  if (tag === 'font') {
    const color = el.getAttribute('color');
    if (color) next.textColor = cssColorToHex(color) ?? next.textColor;
  }
  const style = el.style;
  if (style.color) next.textColor = cssColorToHex(style.color) ?? next.textColor;
  if (style.backgroundColor) {
    next.highlight = cssColorToHex(style.backgroundColor) ?? next.highlight;
  }
  const weight = style.fontWeight;
  if (weight === 'bold' || weight === '700' || Number(weight) >= 600) next.bold = true;
  if (style.fontStyle === 'italic') next.italic = true;
  if (style.textDecoration.includes('underline')) next.underline = true;
  if (style.textDecoration.includes('line-through')) next.strikethrough = true;
  if (style.verticalAlign === 'super') {
    next.superscript = true;
    next.subscript = false;
  }
  if (style.verticalAlign === 'sub') {
    next.subscript = true;
    next.superscript = false;
  }
  return next;
}

function marksFromBag(bag: MarkBag): readonly QXTextMark[] {
  const marks: QXTextMark[] = [];
  if (bag.bold) marks.push(createQXTextMark('bold'));
  if (bag.italic) marks.push(createQXTextMark('italic'));
  if (bag.underline) marks.push(createQXTextMark('underline'));
  if (bag.strikethrough) marks.push(createQXTextMark('strikethrough'));
  if (bag.superscript) marks.push(createQXTextMark('superscript'));
  if (bag.subscript) marks.push(createQXTextMark('subscript'));
  if (bag.textColor) {
    try {
      marks.push(createQXTextFormatMark('text-color', bag.textColor));
    } catch {
      /* non-canonical color — omit */
    }
  }
  if (bag.highlight) {
    try {
      marks.push(createQXTextFormatMark('highlight', bag.highlight));
    } catch {
      /* omit */
    }
  }
  return marks;
}

function pushInline(inlines: QXTextInline[], text: string, bag: MarkBag): void {
  if (text.length === 0) return;
  inlines.push(createQXTextInline(text, marksFromBag(bag)));
}

function walkInlines(node: Node, bag: MarkBag, inlines: QXTextInline[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    pushInline(inlines, node.textContent ?? '', bag);
    return;
  }
  if (!(node instanceof HTMLElement)) return;
  const tag = node.tagName.toLowerCase();
  if (tag === 'br') {
    pushInline(inlines, '\n', bag);
    return;
  }
  if (tag === 'img') return;
  const next = mergeMarks(bag, node);
  for (const child of Array.from(node.childNodes)) {
    walkInlines(child, next, inlines);
  }
}

/** Map an element’s inline DOM → tip inlines (marks preserved). */
export function inlinesFromElement(el: HTMLElement): readonly QXTextInline[] {
  const inlines: QXTextInline[] = [];
  for (const child of Array.from(el.childNodes)) {
    walkInlines(child, EMPTY_MARKS, inlines);
  }
  if (inlines.length === 0) return [createQXTextInline('')];
  return inlines;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Tip marks → draft HTML wrappers (`sup` / `sub` included). */
export function wrapMarks(text: string, marks: readonly QXTextMark[]): string {
  let html = escapeHtml(text).replace(/\n/g, '<br>');
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        html = `<b>${html}</b>`;
        break;
      case 'italic':
        html = `<i>${html}</i>`;
        break;
      case 'underline':
        html = `<u>${html}</u>`;
        break;
      case 'strikethrough':
        html = `<s>${html}</s>`;
        break;
      case 'superscript':
        html = `<sup>${html}</sup>`;
        break;
      case 'subscript':
        html = `<sub>${html}</sub>`;
        break;
      case 'text-color':
        html = `<span style="color:${mark.value}">${html}</span>`;
        break;
      case 'highlight':
        html = `<span style="background-color:${mark.value}">${html}</span>`;
        break;
      default:
        break;
    }
  }
  return html;
}

export function inlinesToHtml(inlines: readonly QXTextInline[]): string {
  return inlines
    .map((inline) => (inline.type === 'note-ref' ? '' : wrapMarks(inline.text, inline.marks)))
    .join('');
}
